/**
 * Moon Phase Calculation Utilities
 * Calculates the current moon phase based on astronomical data
 */

export type MoonPhaseName =
  | "New Moon"
  | "Waxing Crescent"
  | "First Quarter"
  | "Waxing Gibbous"
  | "Full Moon"
  | "Waning Gibbous"
  | "Last Quarter"
  | "Waning Crescent";

export interface MoonPhaseData {
  phaseName: MoonPhaseName;
  phaseEmoji: string;
  illumination: number; // 0-100%
  age: number; // Days since new moon (0-29.53)
  description: string;
}

/**
 * Calculate moon phase for a given date
 * Uses the astronomical algorithm based on lunar cycles
 * 
 * @param date - The date to calculate moon phase for (defaults to today)
 * @returns MoonPhaseData object with phase information
 */
export function getMoonPhase(date: Date = new Date()): MoonPhaseData {
  // Known new moon reference: January 6, 2000, 18:14 UTC
  const knownNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
  
  // Lunar cycle length in days
  const lunarCycle = 29.53058867; // Average synodic month
  
  // Calculate days since known new moon
  const daysSinceKnownNewMoon = (date.getTime() - knownNewMoon.getTime()) / (1000 * 60 * 60 * 24);
  
  // Calculate current position in lunar cycle
  const age = daysSinceKnownNewMoon % lunarCycle;
  
  // Calculate illumination percentage
  const illumination = (1 - Math.cos((age / lunarCycle) * 2 * Math.PI)) / 2 * 100;
  
  // Determine phase name based on age
  let phaseName: MoonPhaseName;
  let phaseEmoji: string;
  let description: string;
  
  if (age < 1.84566) {
    phaseName = "New Moon";
    phaseEmoji = "🌑";
    description = "A time for new beginnings, setting intentions, and inner reflection.";
  } else if (age < 7.38264) {
    phaseName = "Waxing Crescent";
    phaseEmoji = "🌒";
    description = "Energy is building. A time for planning and taking initial action.";
  } else if (age < 9.22830) {
    phaseName = "First Quarter";
    phaseEmoji = "🌓";
    description = "Half illuminated. A time for decision-making and overcoming challenges.";
  } else if (age < 14.76529) {
    phaseName = "Waxing Gibbous";
    phaseEmoji = "🌔";
    description = "Nearly full. A time for refinement, adjustment, and preparation.";
  } else if (age < 16.61096) {
    phaseName = "Full Moon";
    phaseEmoji = "🌕";
    description = "Peak energy and illumination. A time for completion and celebration.";
  } else if (age < 22.14795) {
    phaseName = "Waning Gibbous";
    phaseEmoji = "🌖";
    description = "Energy waning. A time for gratitude, sharing, and teaching.";
  } else if (age < 23.99361) {
    phaseName = "Last Quarter";
    phaseEmoji = "🌗";
    description = "Half illuminated. A time for release, forgiveness, and letting go.";
  } else {
    phaseName = "Waning Crescent";
    phaseEmoji = "🌘";
    description = "Final phase. A time for rest, surrender, and preparation for renewal.";
  }
  
  return {
    phaseName,
    phaseEmoji,
    illumination: Math.round(illumination * 10) / 10,
    age: Math.round(age * 10) / 10,
    description,
  };
}

/**
 * Get the next full moon date
 * @param fromDate - Starting date (defaults to today)
 * @returns Date of next full moon
 */
export function getNextFullMoon(fromDate: Date = new Date()): Date {
  const lunarCycle = 29.53058867;
  const currentPhase = getMoonPhase(fromDate);
  
  // Days until full moon (age 14.76529)
  let daysUntilFull: number;
  if (currentPhase.age < 14.76529) {
    daysUntilFull = 14.76529 - currentPhase.age;
  } else {
    daysUntilFull = lunarCycle - currentPhase.age + 14.76529;
  }
  
  const nextFullMoon = new Date(fromDate);
  nextFullMoon.setDate(nextFullMoon.getDate() + Math.ceil(daysUntilFull));
  
  return nextFullMoon;
}

/**
 * Get the next new moon date
 * @param fromDate - Starting date (defaults to today)
 * @returns Date of next new moon
 */
export function getNextNewMoon(fromDate: Date = new Date()): Date {
  const lunarCycle = 29.53058867;
  const currentPhase = getMoonPhase(fromDate);
  
  // Days until new moon
  const daysUntilNew = lunarCycle - currentPhase.age;
  
  const nextNewMoon = new Date(fromDate);
  nextNewMoon.setDate(nextNewMoon.getDate() + Math.ceil(daysUntilNew));
  
  return nextNewMoon;
}

/**
 * Check if moon phase aligns with menstrual cycle phase
 * Some research suggests correlation between lunar and menstrual cycles
 */
export function getMoonCycleAlignment(
  cycleDay: number,
  moonPhase: MoonPhaseData
): {
  aligned: boolean;
  message: string;
} {
  // New moon typically aligns with menstruation
  // Full moon typically aligns with ovulation
  
  if (cycleDay >= 1 && cycleDay <= 5 && moonPhase.phaseName === "New Moon") {
    return {
      aligned: true,
      message: "Your cycle aligns with the new moon - a powerful time for renewal.",
    };
  }
  
  if (cycleDay >= 12 && cycleDay <= 16 && moonPhase.phaseName === "Full Moon") {
    return {
      aligned: true,
      message: "Your cycle aligns with the full moon - peak energy and fertility.",
    };
  }
  
  return {
    aligned: false,
    message: "Moon and cycle phases are independent but both influence your energy.",
  };
}

